import { BgImage } from "@prmf/site-components";
import { Bedroom } from "@prmf/site-data";
import { routes, t } from "@prmf/site-router";
import { Link, NavLink, useLoaderData } from "@remix-run/react";
import { twJoin } from "tailwind-merge";

export function SlidingList({ bedrooms }: { bedrooms: Bedroom[] }) {
  return bedrooms.map((k) => {
    return (
      <NavLink to={k.slug} key={k.slug} className={"block even:bg-gray-100"}>
        <div className="container grid gap-10 py-10 sm:grid-cols-2 lg:grid-cols-3 lg:gap-20">
          <div>
            <div className="mt-4">
              <div className="mb-2">
                <h3 className="text-sm font-semibold">{k.name}</h3>
                <div className="md:h-5" />
              </div>
            </div>
            <p
              className="border-t-4 pt-2 text-xs"
              style={{ borderColor: k.accentColor }}
            >
              {k.description}
            </p>
          </div>
          <div className="grid gap-5 lg:col-span-2 lg:grid-cols-2">
            <BgImage
              className="aspect-[3/3.8] max-h-[80vh] rounded-sm bg-zinc-100"
              src={`/media/bedrooms/${k.slug}-hero.webp`}
            />
            <BgImage
              className="hidden rounded-sm bg-zinc-100 lg:block"
              src={`/media/bedrooms/${k.slug}-hero-02.webp`}
            />
          </div>
        </div>
      </NavLink>
    );
  });
}

function HingedList(props: { bedrooms: Bedroom[] }) {
  return (
    <div className="bg-gray-100">
      <div className="container mx-auto max-w-5xl space-y-10 py-20">
        <h3 className="section-title mx-auto w-max">Hinged Wardrobe Doors</h3>
        <div className="grid gap-10 sm:grid-cols-2">
          {props.bedrooms.map((item) => (
            <Link key={item.slug} to={`/bedrooms/${item.slug}`}>
              <BgImage
                src={`/media/bedrooms/${item.slug}-hero.webp`}
                className="aspect-square bg-cover"
              />
              <div className="mt-4 max-w-md space-y-2">
                <h4 className="font-bold">{item.name}</h4>
                <div
                  className="h-1 w-full bg-neutral-100"
                  style={{ backgroundColor: item.accentColor }}
                />
                <p className="text-sm">{item.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function BedroomList() {
  const data = useLoaderData<{ bedrooms: Bedroom[] }>();
  const bedrooms = data.bedrooms.reduce<{
    sliding: Bedroom[];
    hinged: Bedroom[];
  }>(
    (p, c) => {
      const isHinged = ["BSLH", "BSHH"].includes(c.group);
      isHinged ? p.hinged.push(c) : p.sliding.push(c);
      return p;
    },
    { sliding: [], hinged: [] },
  );

  return (
    <div>
      <BgImage
        src={t(routes.bedrooms, "image")}
        className={twJoin(
          "relative",
          "grid aspect-[4/3] max-h-[40vh] place-items-center",
          "bg-gray-400 text-center text-xl font-bold text-white",
        )}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative">
          <h2 className="font-semibold">Elevate your project</h2>
          <h3 className="font-light">
            A selection of sliding and hinged fitted wardrobe solutions
          </h3>
        </div>
      </BgImage>
      <div>
        <SlidingList bedrooms={bedrooms.sliding} />
      </div>
      <HingedList bedrooms={bedrooms.hinged} />
    </div>
  );
}
